import dayjs from "dayjs";
import { SurveyPDF } from "survey-pdf";

// Builds and downloads the "Source Doc" PDF of a visit's eCRF blocks, with or without the patient's answers
const exportEcrfSourceDoc = ({
  surveyData,
  visit,
  trial,
  infoData,
  includeAnswers,
  includeIds = true,
}) => {
  const removeVisibilityConditions = (element) => {
    if (element.type === "panel" && element.elements) {
      return {
        ...element,
        elements: element.elements.map(removeVisibilityConditions),
      };
    } else if (element.type === "matrix" && element.columns) {
      return {
        ...element,
        columns: element.columns.map((column) => ({
          ...column,
          visibleIf: undefined,
        })),
        rows: element.rows.map((row) => ({
          ...row,
          visibleIf: undefined,
        })),
        visibleIf: undefined,
      };
    } else if (element.type === "matrixdynamic" || element.type === "matrixdropdown") {
      return {
        ...element,
        columns: element.columns.map((column) => ({
          ...column,
          visibleIf: undefined,
        })),
        visibleIf: undefined,
      };
    } else {
      return { ...element, visibleIf: undefined };
    }
  };

  const formatElements = (element) => {
    // Helper to combine title and ID
    // Only add ID if both title and name exist
    const getTitleWithId = (element) => {
      if (typeof element === "object" && element !== null) {
        const title = element.title || element.text || "";
        const name = element.name || element.value || "";

        if (title && name) {
          return includeIds ? `${title} [${name}]` : title;
        } else if (title) {
          return title;
        } else if (name) {
          return includeIds ? `[${name}]` : name;
        }
      }
      // If element is a string or null/undefined
      return element !== undefined && element !== null ? String(element) : "No value available";
    };

    if (element.type === "panel" && element.elements) {
      return {
        ...element,
        elements: element.elements.map(formatElements),
      };
    } else if (element.type === "matrix" && element.columns) {
      return {
        ...element,
        columns: element.columns.map((column) => ({
          ...column,
          title: getTitleWithId(column),
        })),
        rows: element.rows.map((row) => ({
          ...row,
          text: getTitleWithId(row),
        })),
      };
    } else if (element.type === "matrixdynamic" || element.type === "matrixdropdown") {
      return {
        ...element,
        columns: element.columns.map((column) => ({
          ...column,
          title: getTitleWithId(column),
          value: column.value ?? "",
        })),
        rows: element.rows
          ? element.rows.map((row) => ({
              ...row,
              text: getTitleWithId({ title: row.text, name: row.value }),
              value: row.value ?? "",
            }))
          : [],
      };
    } else {
      return {
        ...element,
        title: getTitleWithId(element),
        value: element.value ?? "", // keep value as is
      };
    }
  };

  let pages = [];
  let answers = {};
  surveyData?.forEach((survey) => {
    survey.surveyJson?.pages?.forEach((page) => {
      const modifiedPage = {
        ...page,
        elements: page.elements?.map(removeVisibilityConditions).map(formatElements),
      };
      pages.push({ ...modifiedPage, title: survey.surveyJson?.title });
    });
    if (includeAnswers) {
      answers = { ...answers, ...survey.answer_json_obj };
    }
  });

  const surveyPdf = new SurveyPDF({ pages });
  if (includeAnswers) {
    surveyPdf.data = answers;
  }

  const timestamp = dayjs().format("YYYYMMDDTHHmmss");

  let visitInfo;
  if (visit.visit_number === 0) {
    visitInfo = visit?.visit_name || "Baseline";
  } else if (visit.visit_number === -1) {
    visitInfo = visit?.visit_name || "Adverse_Event";
  } else if (visit.visit_number === -2) {
    visitInfo = visit?.visit_name || "Unscheduled_Event";
  } else if (visit.visit_number === -3) {
    visitInfo = visit?.visit_name || "Screening_Visit";
  } else {
    visitInfo = visit?.visit_name || `Visit_${visit.visit_number}`;
  }

  const siteParticipantLabel = infoData ? `${infoData.site_id}-${infoData.participant_id}_` : "";
  const customFilename = `${timestamp}_${trial?.name}_${siteParticipantLabel}${visitInfo}`;

  surveyPdf.onRenderFooter.add((_, canvas) => {
    const date = dayjs().format("Do MMMM YYYY");
    const visitLabel = visitInfo.replace("_", " ");
    const siteParticipantText = infoData
      ? `SiteID-SubjectsID: ${infoData.site_id}-${infoData.participant_id}    `
      : "";
    const headerText = `Trial: ${trial?.name}    Visit: ${visitLabel}    ${siteParticipantText}Date: ${date}`;

    canvas.drawText({
      text: headerText,
      fontSize: 10,
      isBold: true,
    });
  });
  surveyPdf.save(customFilename);
};

export default exportEcrfSourceDoc;
